import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { FormModal } from '../Modals';
import { useChannelForm, ChannelType } from './useChannelForm';

import { useIntakeSendForm } from '../IntakeForms/useIntakeSendForm';
import { useGetChannelModalTexts } from './useGetChannelModalTexts';
import { CreateButtonProps } from '../UI/Buttons/CreateButton';
import { ChannelsPageLayoutHeaderContext } from '../Layout/ChannelsPageLayout';
import { SecondarySidebarV2 } from '../UI/SecondarySidebar.v2';

const CREATE_CHANNEL_FALLBACK_TITLE = 'Create a new channel';

export interface ChannelsSidebarProps {
  entity: ChannelType;
  onCreate: (values: any) => void;
  initialCreateFormValue: any;
  modalTitle?: string;
  hasChannels?: boolean;
  newButtonProps?: Partial<CreateButtonProps>;
  newChannelAction?: string;
  onSecondaryOptionClick: (option: string) => void;
  noCreateAction?: boolean;
  noSpace?: boolean;
}

const ChannelsSidebarV2: React.FC<ChannelsSidebarProps> = ({
  onCreate,
  entity,
  children,
  initialCreateFormValue,
  modalTitle,
  hasChannels = true,
  newButtonProps,
  newChannelAction,
  onSecondaryOptionClick,
  noCreateAction = false,
  noSpace = false,
}) => {
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const searchValue = useSelector((state: RootState) => state.ui.searchValue);
  const moduleSettings = useSelector(
    (state: RootState) => state.settings.moduleSettings,
  );
  const [openCreateModal, setOpenCreateModal] = React.useState(false);

  const handleOpenModal = () => setOpenCreateModal(true);
  const handleCloseModal = () => setOpenCreateModal(false);
  const { positiveAction } = useGetChannelModalTexts(entity, moduleSettings);
  const {
    setCreateButtonProps,
    setChannelsListToolbarHidden,
  } = React.useContext(ChannelsPageLayoutHeaderContext);
  // According to the entity type return a specific
  // form for the modal
  const buildChannelFormByEntity = () => {
    if (entity === 'forms') {
      return useIntakeSendForm();
    }

    return useChannelForm(entity);
  };

  /**
   * Based on the action that is selected in menu
   * we should perform new channel action to open modal
   * or call prop function to handle secondary option
   * @param optionValue menu option selected
   */
  const handleClickOptions = (optionValue: string) => {
    if (optionValue === newChannelAction) {
      handleOpenModal();
    } else {
      onSecondaryOptionClick(optionValue);
    }
  };

  const createButtonProps = newButtonProps
    ? {
        onClickOption: handleClickOptions,
        ...newButtonProps,
      }
    : {
        onClick: handleOpenModal,
      };

  React.useEffect(() => {
    // if there are channels, show the channels list toolbar
    if (hasChannels) {
      setChannelsListToolbarHidden(false);
    }

    // if it is an internal user and create action is allowed
    // set the create button props
    if (!isClient && !noCreateAction) {
      setCreateButtonProps(createButtonProps);
    }

    return () => {
      // when channels sidebar unmounts, reset the create button props
      // and hide the channels list toolbar.
      // Also check if search term is empty to avoid hiding the toolbar
      // when user is searching for a channel.
      if (!searchValue) {
        setCreateButtonProps(null);
        setChannelsListToolbarHidden(true);
      }
    };
  }, [hasChannels, noCreateAction, searchValue]);

  return (
    <SecondarySidebarV2 hidden={!hasChannels} noSpace={noSpace}>
      {children}
      <FormModal
        buttonLabel={positiveAction}
        initialFormValue={initialCreateFormValue}
        entityName="channel"
        handleSave={onCreate}
        useFormHook={() => buildChannelFormByEntity()}
        open={openCreateModal}
        onClose={handleCloseModal}
        createButtonProps={{ fullWidth: true }}
        title={modalTitle || CREATE_CHANNEL_FALLBACK_TITLE}
      />
    </SecondarySidebarV2>
  );
};

export default React.memo(ChannelsSidebarV2);
